import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useWebSocket } from '../WebSocketContext';
import { Box, Typography, Card, CardContent, Divider, IconButton, Tooltip, MenuItem, FormControl, Select, Paper, Chip, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import loadingAnimation from '../../images/flying-saucer.gif';
import AnalyticsIcon from '@mui/icons-material/Insights';
import { AreaChart, Area, XAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { ReferenceLine, YAxis } from '../Charts/RechartsComponents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Import expand icon
import ExpandLessIcon from '@mui/icons-material/ExpandLess'; // Import collapse icon
import Collapse from '@mui/material/Collapse'; // Import Collapse
import VisibilityOffIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/CalendarMonth';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { useParams } from 'react-router-dom';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, aggregation }) => {
  if (active && payload && payload.length > 0) {
    let formattedDate;
    if (aggregation === 'week') {
      formattedDate = `Week of ${new Date(label).toLocaleDateString()}`;
    } else if (aggregation === 'month') {
      const [year, month] = label.split('-');
      const dateObj = new Date(year, month - 1);
      formattedDate = dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });
    } else {
      formattedDate = new Date(label).toLocaleDateString();
    }

    return (
      <Paper
        elevation={3}
        sx={{
          padding: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: 'px solid #ccc',
          borderRadius: '8px',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">{formattedDate}</Typography>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          Events: <span style={{ fontWeight: 'bold', color: '#3f51b5' }}>{payload[0].value}</span>
        </Typography>
      </Paper>
    );
  }
  return null;
};

const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';
const CHART_HEIGHT = 180;

const TimeLineTrendChart = ({
  currentFilter = '', // Empty = no text filter
  currentShowHidden = true, // Show all by default for chart
  totalEventsCount = 0,
  initialExpanded = true,
  onExpandedChange,
  variant = 'standalone'
}) => {
  const { t } = useTranslation();
  const [aggregation, setAggregation] = useState('month'); // Changed default from 'day' to 'month'
  const [timeRange, setTimeRange] = useState('6month'); // Changed default from '6month' to '6month'
  const [showEmptyDates, setShowEmptyDates] = useState(true); // Added state for showing empty dates
  const [expanded, setExpanded] = useState(initialExpanded); // Add state for expand/minimize
  const [showInfo, setShowInfo] = useState(false); // State for info tooltip
  const { ws } = useWebSocket();
  const { caseid } = useParams();
  const isWidgetVariant = variant === 'widget';
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldStackHeader = isWidgetVariant && isSmallScreen;
  const isCollapsedWidget = isWidgetVariant && !expanded;
  const [serverChartData, setServerChartData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [chartTotalEvents, setChartTotalEvents] = useState(0);
  const lastFetchRef = useRef(null); // Track last fetch to prevent duplicates

  const handleAggregationChange = (event) => { // Added handler for dropdown
    setAggregation(event.target.value);
  };

  const handleTimeRangeChange = (event) => { // Added handler for time range
    setTimeRange(event.target.value);
  };

  const handleShowEmptyDatesToggle = () => { // New handler for IconButton
    setShowEmptyDates(prev => !prev);
  };

  useEffect(() => {
    if (typeof initialExpanded === 'boolean') {
      setExpanded(initialExpanded);
    }
  }, [initialExpanded]);

  const toggleExpanded = () => { // Add handler to toggle state
    setExpanded(prev => {
      const next = !prev;
      if (onExpandedChange) {
        onExpandedChange(next);
      }
      return next;
    });
  };

  const toggleInfo = () => {
    setShowInfo(prev => !prev);
  };
  // No longer need client-side calculation - server provides the data

  // Process server data and optionally fill empty dates on client side
  const chartData = useMemo(() => {
    if (!serverChartData || serverChartData.length === 0) {
      console.log('No server chart data');
      return [];
    }

    console.log('Processing chart data, aggregation:', aggregation, 'showEmptyDates:', showEmptyDates);
    console.log('Server data:', serverChartData);

    // Always return server data as-is (properly sorted)
    // The server already handles aggregation correctly
    const sorted = [...serverChartData].sort((a, b) => {
      // For week aggregation, dates are in YYYY-MM-DD format (Monday)
      // For month, they're YYYY-MM
      // For day, they're YYYY-MM-DD
      const dateA = new Date(a.date + (aggregation === 'month' ? '-01' : ''));
      const dateB = new Date(b.date + (aggregation === 'month' ? '-01' : ''));
      return dateA.getTime() - dateB.getTime();
    });
    console.log('Sorted chart data (not showing empty):', sorted);

    // Only fill empty dates if showEmptyDates is true
    if (!showEmptyDates) {
      console.log('Returning sorted data without filling');
      return sorted;
    }

    console.log('Filling empty dates...');

    // Fill missing periods with zero counts
    if (sorted.length === 0) return sorted;

    // Create a map of existing data
    const dataMap = {};
    sorted.forEach(item => {
      dataMap[item.date] = item.count;
    });

    const filled = [];

    // Parse the first and last dates from server (these are already in correct format)
    const firstDateStr = sorted[0].date;
    const lastDateStr = sorted[sorted.length - 1].date;

    if (aggregation === 'month') {
      // For months, generate YYYY-MM format
      const [startYear, startMonth] = firstDateStr.split('-').map(Number);
      const [endYear, endMonth] = lastDateStr.split('-').map(Number);

      let year = startYear;
      let month = startMonth;

      while (year < endYear || (year === endYear && month <= endMonth)) {
        const period = `${year}-${String(month).padStart(2, '0')}`;
        filled.push({
          date: period,
          count: dataMap[period] || 0
        });

        month++;
        if (month > 12) {
          month = 1;
          year++;
        }
      }
    } else {
      // For day and week, work with actual dates
      const firstDate = new Date(firstDateStr + 'T00:00:00');
      const lastDate = new Date(lastDateStr + 'T00:00:00');
      const increment = aggregation === 'week' ? 7 : 1;

      let current = new Date(firstDate);
      while (current <= lastDate) {
        const period = current.toISOString().split('T')[0];
        filled.push({
          date: period,
          count: dataMap[period] || 0
        });
        current.setDate(current.getDate() + increment);
      }
    }

    console.log('Filled chart data with empty dates:', filled);
    return filled;
  }, [serverChartData, showEmptyDates, aggregation]);
  // Calculate statistics from server data
  const totalEvents = chartTotalEvents || chartData.reduce((sum, item) => sum + item.count, 0);
  const avgEvents = chartData.length > 0 ? totalEvents / chartData.length : 0;

  // Add WebSocket listener for chart data
  useEffect(() => {
    if (!ws) return;

    const handleChartData = (event) => {
      try {
        const parsedMessage = JSON.parse(event.data);
        if (parsedMessage.command === 'getTimelineTrendData') {
          // Handle nested data structure: parsedMessage.data.data
          const responseData = parsedMessage.data?.data || parsedMessage.data || {};
          console.log('Chart data received:', responseData);
          console.log('Chart data array:', responseData.chartData);
          setServerChartData(responseData.chartData || []);
          setChartTotalEvents(responseData.totalEvents || 0);
          setIsLoadingChart(false);
        }
      } catch (error) {
        console.error('Error parsing chart data:', error);
        setIsLoadingChart(false);
      }
    };

    // Add the event listener
    ws.addEventListener('message', handleChartData);

    // Clean up the event listener when the component unmounts
    return () => {
      ws.removeEventListener('message', handleChartData);
    };
  }, [ws]);
  // Request chart data from the server when parameters change
  useEffect(() => {
    if (!expanded) {
      setIsLoadingChart(false);
      lastFetchRef.current = null;
      return;
    }

    if (ws && ws.readyState === WebSocket.OPEN && caseid && expanded) {
      // Create a stable key for this request
      const requestKey = JSON.stringify({
        aggregation,
        timeRange,
        filter: currentFilter,
        showHidden: currentShowHidden
      });

      // Skip if this is the same as last fetch
      if (lastFetchRef.current === requestKey) {
        return;
      }

      lastFetchRef.current = requestKey;
      setIsLoadingChart(true);
      ws.send(JSON.stringify({
        command: 'getTimelineTrendData',
        caseid: parseInt(caseid),
        aggregation: aggregation,
        timeRange: timeRange,
        showEmptyDates: false, // Always send false - empty dates will be generated on client side
        filter: currentFilter,
        showHidden: currentShowHidden
      }));
    }
  }, [expanded, aggregation, timeRange, ws, caseid, currentFilter, currentShowHidden]);

  const Container = isWidgetVariant ? Box : Card;
  const ContentContainer = isWidgetVariant ? Box : CardContent;
  const containerSx = isWidgetVariant ? {} : {
    boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)',
    borderRadius: '16px',
    border: `1px solid ${lightPurple}`,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    marginTop: '10px !important',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 16px 32px rgba(87, 87, 176, 0.2)',
      transform: 'translateY(-5px)'
    },
  };
  const contentSx = isWidgetVariant
    ? {
      p: isCollapsedWidget ? 0 : { xs: 1, sm: 1.5 },
      backgroundColor: isCollapsedWidget ? 'transparent' : 'rgba(248, 250, 252, 0.4)',
      boxSizing: 'border-box'
    }
    : { padding: '20px' };

  return (
    <Container sx={containerSx}>
      <ContentContainer sx={contentSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: shouldStackHeader ? 'flex-start' : 'space-between',
            alignItems: shouldStackHeader ? 'stretch' : (isWidgetVariant ? 'flex-start' : 'center'),
            flexWrap: shouldStackHeader ? 'nowrap' : 'wrap',
            flexDirection: shouldStackHeader ? 'column' : 'row',
            gap: shouldStackHeader ? 0.5 : (isWidgetVariant ? 0.5 : 1),
            mb: isWidgetVariant
              ? (isCollapsedWidget ? 0 : (shouldStackHeader ? 0.75 : 1))
              : 2,
            pb: isWidgetVariant
              ? (isCollapsedWidget ? 0 : (shouldStackHeader ? 0.25 : 0.75))
              : 0,
            borderBottom: isWidgetVariant && !isCollapsedWidget
              ? '1px solid rgba(87, 87, 176, 0.12)'
              : 'none',
            position: shouldStackHeader ? 'relative' : 'static',
            pr: shouldStackHeader ? 5 : 0,
            ...(isCollapsedWidget ? {
              backgroundColor: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 6px 10px rgba(87, 87, 176, 0.04)',
              borderRadius: '16px',
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.75, sm: 1 }
            } : {})
          }}
        >
          <Typography variant={isWidgetVariant ? 'h6' : 'h6'} fontWeight={isWidgetVariant ? 600 : 'bold'} sx={{ display: 'flex', alignItems: 'center', color: primaryColor, textShadow: '0px 1px 2px rgba(87, 87, 176, 0.15)', ...(isWidgetVariant ? { fontSize: { xs: '0.92rem', sm: '1.05rem' }, lineHeight: 1.4 } : {}) }}>
            <AnalyticsIcon sx={{ mr: 1.5, verticalAlign: 'middle', color: primaryColor }} />
            {t('analytics.eventsTrending')}
            <IconButton
              size="small"
              onClick={toggleInfo}
              sx={isWidgetVariant ? {
                ml: 1,
                width: 30,
                height: 30,
                color: primaryColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(87, 87, 176, 0.08)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              } : {
                ml: 1.5,
                width: 30,
                height: 30,
                color: primaryColor,
                '&:hover': {
                  backgroundColor: 'rgba(87, 87, 176, 0.08)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Typography>
          <IconButton
            onClick={toggleExpanded}
            size={isWidgetVariant ? 'small' : 'medium'}
            sx={isWidgetVariant ? {
              backgroundColor: 'transparent',
              color: primaryColor,
              width: 32,
              height: 32,
              position: shouldStackHeader ? 'absolute' : 'static',
              top: shouldStackHeader ? 0 : 'auto',
              right: shouldStackHeader ? 0 : 'auto',
              zIndex: shouldStackHeader ? 1 : 'auto',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(87, 87, 176, 0.08)',
                color: hoverColor,
                transform: 'scale(1.1)'
              },
            } : {
              backgroundColor: 'transparent',
              color: primaryColor,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(87, 87, 176, 0.08)',
                color: hoverColor,
                transform: 'scale(1.1)'
              },
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {showInfo && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'rgba(87, 87, 176, 0.04)', borderRadius: '12px', borderColor: 'rgba(87, 87, 176, 0.2)', display: 'flex', alignItems: 'flex-start', fontSize: '0.9rem', boxShadow: '0 2px 8px rgba(87, 87, 176, 0.1)' }}>
            <InfoIcon sx={{ color: primaryColor, mr: 1.5, mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              This chart shows the frequency of events over time. Adjust the time range and grouping to analyze patterns.
            </Typography>
          </Paper>
        )}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ mb: 3, borderColor: 'rgba(87, 87, 176, 0.15)' }} />

          <Box sx={{
            display: 'flex',
            gap: 1,
            mb: 2,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 90 }}>
                <Select
                  value={aggregation}
                  onChange={handleAggregationChange}
                  sx={{
                    fontSize: '0.8rem',
                    borderRadius: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3f51b5' },
                  }}
                >
                  <MenuItem value="day" sx={{ fontSize: '0.8rem' }}>Day</MenuItem>
                  <MenuItem value="week" sx={{ fontSize: '0.8rem' }}>Week</MenuItem>
                  <MenuItem value="month" sx={{ fontSize: '0.8rem' }}>Month</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 110 }}>
                <Select
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  sx={{
                    fontSize: '0.8rem',
                    borderRadius: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3f51b5' },
                  }}
                >
                  <MenuItem value="1month" sx={{ fontSize: '0.8rem' }}>Last Month</MenuItem>
                  <MenuItem value="6month" sx={{ fontSize: '0.8rem' }}>Last 6 Months</MenuItem>
                  <MenuItem value="12month" sx={{ fontSize: '0.8rem' }}>Last Year</MenuItem>
                  <MenuItem value="24month" sx={{ fontSize: '0.8rem' }}>Last 2 Years</MenuItem>
                  <MenuItem value="48month" sx={{ fontSize: '0.8rem' }}>Last 4 Years</MenuItem>
                  <MenuItem value="forever" sx={{ fontSize: '0.8rem' }}>All Time</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={showEmptyDates ? "Hide Empty Dates" : "Show Empty Dates"}>
                <IconButton
                  onClick={handleShowEmptyDatesToggle}
                  size="small"
                  sx={{
                    backgroundColor: showEmptyDates ? 'rgba(63, 81, 181, 0.1)' : 'transparent',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: showEmptyDates ? 'rgba(63, 81, 181, 0.2)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  {showEmptyDates ?
                    <VisibilityIcon fontSize="small" sx={{ color: '#3f51b5' }} /> :
                    <VisibilityOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip
                label={`Total: ${totalEvents}`}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  backgroundColor: 'rgba(63, 81, 181, 0.1)',
                  color: '#3f51b5'
                }}
              />
              <Chip
                label={`Avg: ${avgEvents.toFixed(1)}`}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  color: '#4caf50'
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 0, minHeight: CHART_HEIGHT }}>
            <Box sx={{ minHeight: CHART_HEIGHT }}>
              {isLoadingChart ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: CHART_HEIGHT, width: '100%' }}>
                  <img src={loadingAnimation} alt="Loading..." style={{ width: '50px', height: '50px' }} />
                </Box>
              ) : (
                <>
                  {console.log('Rendering chart with data:', chartData, 'Length:', chartData.length)}
                  <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: -30, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="blue" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="red" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: '#666' }}
                        tickFormatter={(date) => {
                          if (aggregation === 'week') {
                            return `${new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                          } else if (aggregation === 'month') {
                            const [year, month] = date.split('-');
                            const dateObj = new Date(year, month - 1);
                            return dateObj.toLocaleString('default', { month: 'short', year: '2-digit' });
                          }
                          return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        }}
                        stroke="#aaa"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#666' }}
                        stroke="#aaa"
                        allowDecimals={false}
                      />
                      <ReferenceLine y={avgEvents} stroke="#4caf50" strokeDasharray="3 3" />
                      <RechartsTooltip
                        content={<CustomTooltip aggregation={aggregation} />}
                        cursor={{ stroke: 'red', strokeWidth: 1, strokeDasharray: '5 5' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#3f51b5"
                        strokeWidth={2}
                        fill="url(#colorCount)"
                        activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2, fill: '#3f51b5' }}
                        animationDuration={800}
                        isAnimationActive={true}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </>
              )}
            </Box>
          </Box>
        </Collapse>
      </ContentContainer>
    </Container>
  );
};

export default TimeLineTrendChart;
